// EMPLOYEE-PROFILE-1 (2026-07-08) — Admin → Website Publish: approval queue
// for employee promote-to-website requests + the currently-published roster
// + site-data export. Gated by Cap manage_website_publish.
import { useEffect, useState } from "react";
import { apiFetch, fmt } from "../api";
import { StatusPill, EmptyState, Spinner, SectionHeader } from "./ui";

function PayloadPreview({ p }) {
  if (!p) return null;
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 14, background: "var(--bg)", fontSize: 13 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
      <div style={{ color: "var(--text2)" }}>{p.title || <em>no title</em>} · {p.group} · {p.region || "no location"}</div>
      <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 8 }}>{p.phone || "no phone"} · {p.email}</div>
      {p.licenses && <div style={{ fontSize: 12.5, marginBottom: 6 }}><b>Licensed:</b> {p.licenses}</div>}
      {(p.specs || []).length > 0 &&
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {p.specs.map(s => <span key={s} style={{ fontSize: 11.5, padding: "2px 9px", borderRadius: 10, background: "var(--bg3)", border: "1px solid var(--border)" }}>{s}</span>)}
        </div>}
      {(p.bio || []).map((t, i) => <p key={i} style={{ margin: "0 0 7px", color: "var(--text2)" }}>{t}</p>)}
      {!p.has_headshot && <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>No headshot — the site shows a monogram.</div>}
    </div>
  );
}

export default function WebsitePublishPanel() {
  const [requests, setRequests] = useState(null);
  const [published, setPublished] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 6000); };

  const load = async () => {
    try {
      const [r, p] = await Promise.all([
        apiFetch("/admin/website-publish/requests?status=pending"),
        apiFetch("/admin/website-publish/published"),
      ]);
      setRequests(r.requests || []); setPublished(p.published || []);
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); setRequests([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    setBusy(true);
    try {
      await apiFetch(`/admin/website-publish/requests/${id}/${action}`, { method: "POST", body: { note } });
      setOpenId(null); setNote("");
      flash("success", action === "approve" ? "Approved and published — site data exported." : "Request declined.");
      await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const unpublish = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the public website?`)) return;
    setBusy(true);
    try { await apiFetch(`/admin/website-publish/published/${userId}`, { method: "DELETE" }); await load(); }
    catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const setInclude = async (userId, include, name) => {
    if (!include && !window.confirm(`Hide ${name} from the public website? They stay published in Atticus and can be re-included any time.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/admin/website-publish/users/${userId}/include`, { method: "PATCH", body: { include } });
      flash("success", include ? `${name} is included on the website again — site data re-exported.`
                               : `${name} is now hidden from the website — site data re-exported.`);
      await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const exportNow = async () => {
    setBusy(true);
    try {
      const r = await apiFetch("/admin/website-publish/export", { method: "POST" });
      flash("success", `Exported ${r.people} people to ${r.outbox}.`);
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  if (requests === null) return <Spinner label="Loading publish queue…" />;

  const successStyle = { background: "#d1fae5", border: "1px solid #6ee7b7", color: "#065f46", padding: "8px 12px", borderRadius: 4, fontSize: 12, marginBottom: 12 };
  const errorStyle = { background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", padding: "8px 12px", borderRadius: 4, fontSize: 12, marginBottom: 12 };

  return (
    <div>
      {msg && <div style={msg.type === "success" ? successStyle : errorStyle}>{msg.text}</div>}

      <SectionHeader title={`Pending requests (${requests.length})`}
                     subtitle="Employee profiles awaiting approval for the public website" />
      {requests.length === 0 &&
        <EmptyState icon="🌐" title="No pending publish requests"
                    hint="When an employee clicks “Promote to Website” on their Profile page, the request lands here for approval." />}
      {requests.map(r => (
        <div key={r.id} className="card" style={{ marginBottom: 12 }}>
          <div className="card-body">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <b>{r.full_name}</b>
                <span style={{ color: "var(--text3)", fontSize: 12, marginLeft: 10 }}>requested {fmt.datetime(r.requested_at)}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-sm" onClick={() => { setOpenId(openId === r.id ? null : r.id); setNote(""); }}>
                  {openId === r.id ? "Hide preview" : "Review"}
                </button>
              </div>
            </div>
            {openId === r.id && (
              <div style={{ marginTop: 12 }}>
                <PayloadPreview p={r.payload} />
                <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <input placeholder="Optional note (shown to the employee on decline)" value={note}
                         onChange={e => setNote(e.target.value)} style={{ flex: 1, minWidth: 240 }} />
                  <button className="btn btn-sm" onClick={() => decide(r.id, "reject")} disabled={busy}>Decline</button>
                  <button className="btn btn-sm btn-primary" onClick={() => decide(r.id, "approve")} disabled={busy}>
                    Approve &amp; Publish
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      <SectionHeader title={`On the website (${published.length})`}
                     subtitle="Currently published to the Our People section"
                     actions={<button className="btn btn-sm" onClick={exportNow} disabled={busy}>Export site data</button>} />
      {published.length === 0 &&
        <EmptyState icon="👥" title="Nobody is published yet"
                    hint="Approve a request above to publish the first profile." />}
      {published.length > 0 && (
        <table className="tbl-compact" style={{ width: "100%" }}>
          <thead><tr><th>Name</th><th>Title</th><th>Group</th><th>Published</th><th>Headshot</th><th>On website</th><th></th></tr></thead>
          <tbody>
            {published.map(p => (
              <tr key={p.user_id} style={p.website_include === false ? { opacity: .55 } : undefined}>
                <td><b>{p.full_name}</b></td>
                <td>{(p.payload && p.payload.title) || "—"}</td>
                <td>{(p.payload && p.payload.group) || "—"}</td>
                <td>{fmt.date(p.published_at)}</td>
                <td>{p.payload && p.payload.has_headshot ? <StatusPill tone="green" sm>yes</StatusPill> : <StatusPill tone="slate" sm>monogram</StatusPill>}</td>
                <td>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }}
                         title="Include on company website">
                    <input type="checkbox" checked={p.website_include !== false} disabled={busy}
                           onChange={e => setInclude(p.user_id, e.target.checked, p.full_name)} />
                    {p.website_include === false ? <StatusPill tone="slate" sm>hidden</StatusPill> : <StatusPill tone="green" sm>included</StatusPill>}
                  </label>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn btn-sm" onClick={() => unpublish(p.user_id, p.full_name)} disabled={busy}>Unpublish</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 14, fontSize: 12, color: "var(--text3)" }}>
        Approving exports <code>people.json</code> + headshots to the website outbox automatically.
        The public site consumes that bundle when its “Our People” section ships.
      </div>
    </div>
  );
}
